import {CoinsSections_TabNavigationSection_Tab} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  GenericIconName,
  Icon,
  SPACING,
  Text,
  tabletAndUpLinaria,
  useInView,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {RefObject, useCallback, useEffect, useMemo, useRef} from 'react';

export const CoinsTabNavigation: React.FC<{
  tabs: CoinsSections_TabNavigationSection_Tab[];
  activeSectionIndex: number;
  onTabClick: (sectionIndex: number) => void;
  wrapperRef?: RefObject<HTMLDivElement | null>;
}> = ({tabs, activeSectionIndex, onTabClick, wrapperRef}) => {
  const {t} = useTranslation();
  const stripRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const {ref: stickySentinelRef, inView: isSentinelInView} =
    useInView<HTMLDivElement>({initialInView: true});

  // Some sections (e.g. "view all achievements", disclaimer) don't have a
  // matching tab, so map the active section onto the latest tab whose
  // section we've already scrolled into. Before the user scrolls past the
  // first tab's section, fall back to the first tab so the strip never
  // renders without an active pill.
  const activeTabSectionIndex = useMemo(() => {
    let bestSectionIndex: number | undefined;
    for (const tab of tabs) {
      if (
        tab.sectionIndex <= activeSectionIndex &&
        (bestSectionIndex === undefined || tab.sectionIndex > bestSectionIndex)
      ) {
        bestSectionIndex = tab.sectionIndex;
      }
    }
    return bestSectionIndex ?? tabs[0]?.sectionIndex;
  }, [tabs, activeSectionIndex]);

  // Two reasons we don't use chip.scrollIntoView here:
  //   1. scrollIntoView walks every scrollable ancestor — `block: 'start'`
  //      would also try to scroll the wallet body vertically, fighting the
  //      smooth scroll started by scrollToSection.
  //   2. `behavior: 'smooth'` on a second container cancels the body's
  //      smooth scroll mid-flight (browsers only run one smooth scroll at a
  //      time) and parks the clicked section mid-viewport.
  // Setting strip.scrollLeft directly via scrollTo with `behavior: 'auto'`
  // is instant and only touches the strip's horizontal axis.
  const scrollActiveChipIntoStripView = useCallback(() => {
    if (activeTabSectionIndex === undefined) return;
    const chip = chipRefs.current[activeTabSectionIndex];
    const strip = stripRef.current;
    if (!chip || !strip) return;

    const chipLeft = chip.offsetLeft;
    const chipRight = chipLeft + chip.offsetWidth;
    const visibleLeft = strip.scrollLeft;
    const visibleRight = visibleLeft + strip.clientWidth;

    let nextScrollLeft = strip.scrollLeft;
    if (chipLeft < visibleLeft) {
      nextScrollLeft = chipLeft;
    } else if (chipRight > visibleRight) {
      nextScrollLeft = chipRight - strip.clientWidth;
    }

    if (nextScrollLeft !== strip.scrollLeft) {
      strip.scrollTo({left: nextScrollLeft, behavior: 'auto'});
    }
  }, [activeTabSectionIndex]);

  // Defer to rAF and cancel pending frames on re-fire: during a body smooth
  // scroll, the observer flips activeTabSectionIndex many times in quick
  // succession, so this collapses the bursts into one paint-aligned scroll
  // instead of doing a synchronous layout read on every tick.
  useEffect(() => {
    const frame = requestAnimationFrame(scrollActiveChipIntoStripView);
    return () => cancelAnimationFrame(frame);
  }, [scrollActiveChipIntoStripView]);

  const scrollStripByPage = (direction: 1 | -1) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollBy({left: direction * strip.clientWidth, behavior: 'smooth'});
  };

  return (
    <>
      <StickySentinel ref={stickySentinelRef} />
      <Wrapper ref={wrapperRef} data-stuck={!isSentinelInView}>
        <StepperRow>
          <StepperPill>
            <StepperButton
              type="button"
              onClick={() => scrollStripByPage(-1)}
              aria-label={t('Scroll tabs left')}
              data-testid="coinsTabNavigationPrev"
            >
              <Icon
                asset={GenericIconName.ArrowLeft}
                size="XSS"
                fill={COLORS.Black}
              />
            </StepperButton>
            <StepperButton
              type="button"
              onClick={() => scrollStripByPage(1)}
              aria-label={t('Scroll tabs right')}
              data-testid="coinsTabNavigationNext"
            >
              <Icon
                asset={GenericIconName.ArrowRight}
                size="XSS"
                fill={COLORS.Black}
              />
            </StepperButton>
          </StepperPill>
        </StepperRow>
        <Strip ref={stripRef} data-testid="coinsTabNavigation" role="tablist">
          {tabs.map((tab, index) => {
            const isActive = tab.sectionIndex === activeTabSectionIndex;
            return (
              <Chip
                key={`${tab.sectionIndex}-${index}`}
                ref={el => {
                  chipRefs.current[tab.sectionIndex] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-active={isActive ? 'true' : undefined}
                onClick={() => onTabClick(tab.sectionIndex)}
              >
                <Text
                  size="px14"
                  weight={isActive ? 'mediumHeavy' : 'mediumBold'}
                  color={isActive ? 'inherit' : 'Text.Grey'}
                >
                  {tab.label}
                </Text>
              </Chip>
            );
          })}
        </Strip>
      </Wrapper>
    </>
  );
};

const StickySentinel = styled.div`
  height: 1px;
  margin-bottom: -1px;
  opacity: 0;
`;

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${COLORS.White};
  padding: 0 0 ${SPACING['Y.Lg']} 0;
  margin-inline: -${SPACING['X.Lg']};
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s;

  &[data-stuck='true'] {
    border-bottom-color: ${COLORS['Outline.Grey']};
  }
`;

const StepperRow = styled.div`
  display: none;
  padding-inline: ${SPACING['X.Lg']};

  ${tabletAndUpLinaria(`
    display: flex;
    justify-content: flex-end;
    margin-bottom: ${SPACING['Y.Md']};
  `)}
`;

const StepperPill = styled.div`
  display: flex;
  align-items: stretch;
  height: 24px;
  box-sizing: border-box;
  border: 1px solid ${COLORS['Outline.Grey']};
  border-radius: 24px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const StepperButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${SPACING['X.Md']};
  border: 0;
  background: transparent;
  cursor: pointer;

  &:not(:last-child) {
    border-right: 1px solid ${COLORS['Outline.Grey']};
  }

  &:disabled {
    cursor: default;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px auto -webkit-focus-ring-color;
    outline-offset: -2px;
  }
`;

const Strip = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: ${SPACING['X.Md']};
  padding-inline: ${SPACING['X.Lg']};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Chip = styled.button`
  flex: 0 0 auto;
  scroll-margin-inline: ${SPACING['X.Lg']};
  padding: ${SPACING['Y.Md']} ${SPACING['X.Lg']};
  border: 1px solid ${COLORS['Outline.Grey']};
  border-radius: 999px;
  background-color: ${COLORS.White};
  color: ${COLORS['Fill.Black']};
  cursor: pointer;
  white-space: nowrap;

  &[data-active='true'] {
    background-color: ${COLORS['Fill.Black']};
    border-color: ${COLORS['Fill.Black']};
    color: ${COLORS.White};
  }

  &[data-active='true'] > * {
    color: inherit;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px auto Highlight;
    outline: 2px auto -webkit-focus-ring-color;
  }
`;
